/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  Bulb,
  Check,
  ChevronLeft,
  ChevronRight,
  Messages,
  Star,
} from '@saleshandy/icons';
import { IProps } from './types';
import Button from '../../../../../../../../shared/design-system/components/atoms/button';
import {
  getInitialStepValue,
  getPlanDetailsObj,
  getPlanIcon,
  getSelectedPlanProspects,
} from './utils/helper';
import { SelectPlanCTAText } from '../../../../../../../../shared/design-system/components/molecules/pricing-card/types';
import { OverlayTooltip } from '../../../../../../../../shared/design-system/components/overlay';
import { RangeInput } from '../range-input';
import { getCustomScalePlusPlanName, isAnnuallyPlan } from '../../utils/helper';
import { BillingOption } from '../../../../../../../../shared/utils/billing-options';
import { getLeadFinderButtonLabel } from '../../../../../../../../shared/design-system/components/molecules/pricing-card/helpers';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import GlobalRestrictionModal from '../../../../../../../home/components/global-restriction-modal';

const LeadFinderPlanCard: React.FC<IProps> = ({
  plan,
  customScalePlusPlans,
  isPurchaseInProgress,
  handlePlanSelect,
  sendGetLeadFinderSubscriptionResponse,
  planCycle,
  isEmailPhonePlan,
  isDemoAccount,
  isVersionLocked,
  isSelected = false,
}) => {
  const { t } = useTranslation();

  //* State */
  const [rangeInputValue, setRangeInputValue] = useState<number[]>([
    getInitialStepValue(),
  ]);

  const [planDetails, setPlanDetails] = useState(getPlanDetailsObj(plan));

  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );

  const btnLabel = getLeadFinderButtonLabel({
    plan: planDetails,
    sendGetLeadFinderSubscriptionResponse,
    planCycle,
  });

  const getButtonTheme = () => Button.Theme.Ghost;

  const getLeadFinderDiscountChipText = (): string => {
    const isYearly = planCycle === BillingOption.ANNUALLY;
    const isStarter =
      planDetails.planName?.toLowerCase().includes('starter') ??
      plan?.planCode?.toLowerCase().includes('starter') ??
      false;
    if (isYearly) {
      return isStarter ? 'Save $120 annually' : 'Save $240 annually';
    }
    return isStarter ? 'Save $120 on annual plan' : 'Save $240 on annual plan';
  };

  const onRangeInputChange = (v: number[]) => {
    setRangeInputValue(v);

    //* Custom Scale Plus Plan */
    const scalePlusPlan = customScalePlusPlans.find(
      (p) =>
        p.planName ===
        getCustomScalePlusPlanName(
          v[0],
          isAnnuallyPlan(planCycle),
          isEmailPhonePlan,
          isVersionLocked,
        ),
    );

    if (scalePlusPlan) {
      setPlanDetails(getPlanDetailsObj(scalePlusPlan));
    }
  };

  //* Class Variable */
  const cardClassName = classnames([
    'pricing-card',
    {
      'best-value-plan': planDetails.isBestValuePlan,
      'plan-selected': isSelected,
    },
  ]);

  const btnClassName = classnames([
    'ghost-primary-new',
    {
      'ghost-primary-new-disable': btnLabel === SelectPlanCTAText.DowngradePlan,
    },
    {
      'solid-primary-new':
        btnLabel !== SelectPlanCTAText.CurrentPlan &&
        btnLabel !== SelectPlanCTAText.DowngradePlan &&
        planDetails.isBestValuePlan,
    },
    {
      'ghost-secondary-new-disable': btnLabel === SelectPlanCTAText.CurrentPlan,
    },
  ]);

  const onCardClick = () => {
    if (isSelected) return;
    if (btnLabel === SelectPlanCTAText.DowngradePlan) {
      return;
    }
    if (isDemoAccount) {
      setShowGlobalRestrictionModal(true);
      return;
    }
    handlePlanSelect(planDetails.id, planDetails.planName);
  };

  return (
    <div
      className={cardClassName}
      role="button"
      tabIndex={0}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onCardClick();
      }}
      style={{
        cursor:
          isSelected || btnLabel === SelectPlanCTAText.DowngradePlan
            ? 'default'
            : 'pointer',
      }}
    >
      <div className="best-value-plan__label-wrapper">
        {planDetails.isBestValuePlan && (
          <div className="best-value-plan__label">
            <Star color="#FFFFFF" />
            <span className="best-value-plan__label-text">
              {t('labels.most_popular')}
            </span>
          </div>
        )}
      </div>
      <div className="lead-finder-plan-card">
        <div className="card-body">
          <div className="pricing-card__details mb-2">
            <div className="pricing-card__details-header">
              <img src={getPlanIcon(planDetails.planName)} alt="plan-icon" />
              <span className="pricing-card__details-header-title">
                {planDetails.displayName}
              </span>
            </div>
            <div className="pricing-card__details-price-wrapper">
              {planDetails.price ? (
                <>
                  <div className="pricing-card__details-price-wrapper-currency">
                    <span className="pricing-card__details-price-wrapper-currency-amount">
                      ${planDetails.price}
                    </span>

                    <span className="pricing-card__details-price-wrapper-currency-amount-suffix">
                      /Month
                    </span>
                  </div>
                  <div className="pricing-card__details-price-wrapper-discount">
                    <span className="pricing-card__details-price-wrapper-discount-text">
                      {getLeadFinderDiscountChipText()}
                    </span>
                  </div>
                </>
              ) : (
                <div className="mt-1">
                  <Messages
                    width={28}
                    height={28}
                    className="popover-arrow-color-txt"
                  />
                </div>
              )}
            </div>

            {isSelected ? (
              <Button
                variant={Button.Variant.Primary}
                theme={Button.Theme.Solid}
                className="w-100 solid-primary-new"
                disabled
              >
                <Check color="#FFFFFF" className="mr-1" />
                {btnLabel === SelectPlanCTAText.CurrentPlan
                  ? 'Current Plan'
                  : 'Selected'}
              </Button>
            ) : (
              <Button
                variant={Button.Variant.Primary}
                theme={getButtonTheme()}
                onClick={() => {
                  if (isDemoAccount) {
                    setShowGlobalRestrictionModal(true);
                    return;
                  }
                  handlePlanSelect(planDetails.id, planDetails.planName);
                }}
                className={`w-100 ${btnClassName}`}
                isLoading={isPurchaseInProgress}
                disabled={
                  btnLabel === SelectPlanCTAText.DowngradePlan ||
                  isPurchaseInProgress
                }
              >
                {btnLabel === SelectPlanCTAText.CurrentPlan
                  ? btnLabel
                  : btnLabel === SelectPlanCTAText.DowngradePlan
                  ? btnLabel
                  : 'Select Plan'}
              </Button>
            )}

            <div className="pricing-card__features">
              {plan?.isCustomPlan && (
                <div className="range-wrap">
                  <RangeInput
                    value={rangeInputValue}
                    onChange={onRangeInputChange}
                  />
                </div>
              )}
              {plan?.isCustomPlan && (
                <div className="pricing-card__features-slider-control">
                  <button
                    type="button"
                    className="pricing-card__features-slider-control-icon"
                    {...(rangeInputValue[0] > 1 &&
                      accessibleOnClick(() =>
                        onRangeInputChange([rangeInputValue[0] - 1]),
                      ))}
                  >
                    <ChevronLeft height={14} width={14} color="#6B7280" />
                  </button>
                  <span className="pricing-card__features-slider-control-text">
                    <span className="font-semibold">
                      {getSelectedPlanProspects(
                        rangeInputValue[0],
                        isVersionLocked,
                      )}{' '}
                    </span>
                    <span>lead finder credits</span>
                  </span>
                  <button
                    type="button"
                    className="pricing-card__features-slider-control-icon"
                    {...(rangeInputValue[0] < 4 &&
                      accessibleOnClick(() =>
                        onRangeInputChange([rangeInputValue[0] + 1]),
                      ))}
                  >
                    <ChevronRight height={14} width={14} color="#6B7280" />
                  </button>
                </div>
              )}
              <ul className="pricing-card__features-list">
                {planDetails.features?.map(
                  ({
                    label,
                    labelClass,
                    tooltipText,
                    strikeText,
                    highlight,
                  }) => (
                    <li
                      key={label}
                      className={classnames([
                        'pricing-card__features-list-item',
                        {
                          'bg-color': highlight,
                        },
                      ])}
                    >
                      <span className="pricing-card__features-list-item-check">
                        <Check color="#202020" />
                      </span>
                      <>
                        {tooltipText ? (
                          <OverlayTooltip text={tooltipText}>
                            <span
                              className={classnames([
                                'pricing-card__features-list-item-label',
                                'underline-dashed',
                                labelClass,
                              ])}
                            >
                              {strikeText && (
                                <span className="regular-1 gray-txt-15 mr-1 text-decoration-line-through">
                                  {strikeText}
                                </span>
                              )}
                              {label}
                            </span>
                          </OverlayTooltip>
                        ) : (
                          <span
                            className={classnames([
                              'pricing-card__features-list-item-label',
                              labelClass,
                            ])}
                          >
                            {strikeText && (
                              <span className="regular-1 gray-txt-15 mr-1 text-decoration-line-through">
                                {strikeText}
                              </span>
                            )}
                            {label}
                          </span>
                        )}
                      </>
                    </li>
                  ),
                )}
              </ul>
            </div>

            {planDetails.isBestValuePlan && (
              <div className="pricing-card__best-value-plan-insight">
                <Bulb color="#4F46E5" />
                <span className="pricing-card__best-value-plan-insight-text">
                  {isAnnuallyPlan(planCycle)
                    ? t('labels.best_value_plan_insight_yearly')
                    : t('labels.best_value_plan_insight_monthly')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="You’re about to leave the demo account"
        content="Upgrading a plan requires a real Saleshandy account. You’ll be redirected to log in or sign up and continue from your actual account. Do you want to proceed?"
        redirectTo="billing/upgrade-plan/lead-finder"
      />
    </div>
  );
};

export default LeadFinderPlanCard;
